import angular from 'angular';
import { fileToBase64 } from '@utils/upload-arquivo';
import { UploadDocumentosEmLoteService } from '../upload-documentos-em-lote.service';
import {
    IUploadDocumentoCreate,
    IUploadDocumentoPreview,
} from '../models/upload-documentos-em-lote.service.model';
import { IUploadDocumentoForm } from './models/upload-documentos-em-lote.form.model';
import { VisualizacaoPreviaUploadModalService } from '../modals/visualizacao-previa-upload/visualizacao-previa-upload.modal.service';
import { IVisualizacaoPreviaUploadModalData } from '../modals/visualizacao-previa-upload/models/visualizacao-previa-upload.modal.model';

/**
 * Criação do lote.
 *
 * A prévia é um portão obrigatório: `submit()` não cria nada, só pede a projeção.
 * O modal é montado com a resposta da API, que é quem sabe quem está inelegível
 * por documento já liberado e de quem o documento anterior será substituído.
 */
export class UploadDocumentosEmLoteNewController {

    static $inject = [
        '$rootScope',
        '$scope',
        '$state',
        '$filter',
        'UploadDocumentosEmLoteService',
        'VisualizacaoPreviaUploadModalService',
        'NewToaster',
    ];

    public form: angular.IFormController;
    public entity: IUploadDocumentoForm = {} as IUploadDocumentoForm;
    public busy = false;

    constructor(
        public $rootScope: angular.IRootScopeService,
        public $scope: angular.IScope,
        public $state: angular.ui.IStateService,
        public $filter: angular.IFilterService,
        public UploadDocumentosEmLoteService: UploadDocumentosEmLoteService,
        public VisualizacaoPreviaUploadModalService: VisualizacaoPreviaUploadModalService,
        public NewToaster: { pop: (opts: { type: string; title: string; body?: string }) => void },
    ) {
        $scope.$on('meurh_uploadsdocumentos_preview_loaded', (_event, preview: IUploadDocumentoPreview) => {
            this.busy = false;
            const modal = this.VisualizacaoPreviaUploadModalService.open(this.buildPreviewModalData(preview));

            modal.result
                .then((confirmado) => {
                    if (!confirmado) {
                        return;
                    }

                    this.busy = true;
                    this.enviarCriacao();
                })
                .catch(() => { /* modal cancelada */ });
        });

        $scope.$on('meurh_uploadsdocumentos_preview_error', (_event, error) => {
            this.busy = false;
            this.NewToaster.pop({
                type: 'error',
                title: 'Ocorreu um erro ao carregar a prévia do upload.',
                body: this.getErrorMessage(error),
            });
        });

        $scope.$on('meurh_uploadsdocumentos_insert_loaded', () => {
            this.busy = false;
            this.NewToaster.pop({
                type: 'success',
                title: 'Upload de documentos em lote iniciado com sucesso.',
                body: 'Você receberá um e-mail quando o processamento terminar.',
            });
            this.$state.go('uploaddocumentoslote');
        });

        $scope.$on('meurh_uploadsdocumentos_insert_error', (_event, error) => {
            this.busy = false;
            this.NewToaster.pop({
                type: 'error',
                title: 'Ocorreu um erro ao confirmar o upload.',
                body: this.getErrorMessage(error),
            });
        });
    }

    submit() {
        this.form.$submitted = true;

        if (this.form.$invalid) {
            return;
        }

        if (this.isSelecaoPorTrabalhadorSemItens()) {
            this.NewToaster.pop({
                type: 'error',
                title: 'Selecione ao menos um colaborador para ver a prévia.',
            });
            return;
        }

        // sem LIBERAR_REFATORACAO_VISIBILIDADE o select de empresa nao esta no DOM
        // e o ng-required dele nao vale: a empresa vem da sessao.
        if (!this.entity.empresa) {
            this.NewToaster.pop({
                type: 'error',
                title: 'Não foi possível identificar a empresa.',
                body: 'Selecione novamente a empresa no topo da tela e tente outra vez.',
            });
            return;
        }

        const arquivo = this.entity.arquivo?.[0];
        if (!arquivo) {
            this.NewToaster.pop({
                type: 'error',
                title: 'Selecione o arquivo que será enviado.',
            });
            return;
        }

        this.busy = true;

        // a prévia não leva o arquivo: ela projeta o lote a partir do tipo, da
        // competência e do escopo. Converter aqui custaria uma subida do PDF
        // inteiro que a confirmação repetiria.
        this.UploadDocumentosEmLoteService.preview(this.setRequestBody());
    }

    isSubmitDisabled() {
        return this.busy || this.isSelecaoPorTrabalhadorSemItens();
    }

    /**
     * Payload limpo: descarta os campos `_ui` e neutraliza os filtros quando a
     * seleção é por trabalhador.
     */
    setRequestBody(comConteudo: boolean = false): IUploadDocumentoCreate {
        const selecaoPorTrabalhador = this.entity.aba_selecao_colaboradores === 2;

        return {
            tipoarquivo_codigo: this.entity.tipoarquivo_codigo,
            nome: (this.entity.nome || '').trim(),
            ...(comConteudo ? { conteudo: this.entity.conteudo } : {}),
            competenciaano: this.normalizeInteger(this.entity.competenciaano),
            competenciames: this.normalizeInteger(this.entity.competenciames),
            empresa: this.entity.empresa,
            estabelecimento: selecaoPorTrabalhador ? '' : this.entity.estabelecimento,
            departamento_colaboradores: selecaoPorTrabalhador ? null : this.entity.departamento_colaboradores,
            lotacao_colaboradores: selecaoPorTrabalhador ? null : this.entity.lotacao_colaboradores,
            trabalhadores: selecaoPorTrabalhador ? (this.entity.trabalhadores || []) : [],
        };
    }

    buildPreviewModalData(preview: IUploadDocumentoPreview): IVisualizacaoPreviaUploadModalData {
        const selecaoPorTrabalhador = this.entity.aba_selecao_colaboradores === 2;
        const arquivo = this.entity.arquivo?.[0];

        return {
            documento: {
                tipoLabel: this.entity.tipoarquivo_selecionado_ui?.titulo || '-',
                nome: (this.entity.nome || '').trim(),
                competenciaLabel: this.buildCompetenciaLabel(),
                arquivoNome: arquivo?.name || '-',
                arquivoTamanho: arquivo?.size || 0,
            },
            tipoSelecaoColaboradores: selecaoPorTrabalhador ? 'trabalhador' : 'filtro',
            filtrosAplicados: this.buildFiltrosAplicadosResumo(),
            preview,
        };
    }

    buildFiltrosAplicadosResumo() {
        return {
            empresaLabel: this.entity.empresa_selecionada_ui?.razaosocial
                || this.entity.empresa_selecionada_ui?.codigo
                || '-',
            estabelecimentoLabel: this.entity.estabelecimento_selecionado_ui?.nomefantasia || 'Todos',
            departamentoLabel: this.entity.departamento_colaboradores_selecionado_ui?.nome || 'Todos',
            lotacaoLabel: this.entity.lotacao_colaboradores_selecionada_ui?.nome || 'Todos',
        };
    }

    private enviarCriacao() {
        const arquivo = this.entity.arquivo?.[0];
        if (!arquivo) {
            this.busy = false;
            this.NewToaster.pop({
                type: 'error',
                title: 'Selecione o arquivo que será enviado.',
            });
            return;
        }

        fileToBase64(arquivo)
            .then((conteudo) => {
                this.entity.conteudo = conteudo;
                this.UploadDocumentosEmLoteService.criar(this.setRequestBody(true));
            })
            .catch(() => {
                this.busy = false;
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao ler arquivo',
                    body: 'Não foi possível converter o arquivo para envio.',
                });
                // fileToBase64 é Promise nativa: sem isto o digest não roda e a tela
                // fica no estado de carregamento.
                this.$scope.$applyAsync();
            });
    }

    private buildCompetenciaLabel() {
        if (!this.entity.competenciaano) {
            return 'Sem competência';
        }

        // mesmo filtro da listagem e do detalhe: nome do mês, não número.
        const mes = this.entity.competenciames
            ? this.$filter<(mes: number) => string>('mesEnum')(Number(this.entity.competenciames))
            : null;

        return mes ? `${mes}/${this.entity.competenciaano}` : String(this.entity.competenciaano);
    }

    private normalizeInteger(value: number | null | undefined) {
        if (value === null || value === undefined || value === ('' as any)) {
            return null;
        }

        return Number(value);
    }

    private getErrorMessage(error: any) {
        return error?.data?.message || error?.data?.mensagem || error?.data?.erro || undefined;
    }

    private isSelecaoPorTrabalhadorSemItens() {
        return this.entity.aba_selecao_colaboradores === 2 && !this.entity.trabalhadores?.length;
    }
}
